/**
 * Amount grouping and parsing for money inputs.
 *
 * The display side (`formatAmounts` in the financial-aids slice) formats
 * *complete* values for reading. These helpers work on partial input instead —
 * `"300 000,"` mid-typing has no numeric value but must still render — so they
 * live here rather than in a feature's `format.ts`.
 *
 * Kept in sync with the Django app's `assets/js/pages/money-input.js` (the two
 * toolchains can't share a module; same arrangement as selection-field.js and
 * its `SelectionField` counterpart) and with `financial_aids/money.py`, which
 * parses whatever these produce.
 */

/** NBSP, so an amount never wraps mid-number. */
export const GROUP_SEPARATOR = "\u00a0";

const DECIMAL_MARK = ",";
/** Every space a value can pick up from a paste: plain, NBSP, narrow, thin, figure. */
const SPACES = /[\u0020\u00a0\u202f\u2009\u2007]/g;

/**
 * A comma is the decimal mark in French, but a *group* separator in text pasted
 * from an English locale or a spreadsheet ("1,234.56", "1,234,567"). Tell them
 * apart the way `financial_aids/money.py` does — more than one comma, or a comma
 * alongside a dot, means grouping — and drop those commas before parsing, so a
 * paste isn't mistaken for a decimal and truncated to two digits.
 */
function dropGroupingCommas(
  text: string,
  caret: number,
): { text: string; caret: number } {
  const commas = (text.match(/,/g) ?? []).length;
  if (commas < 2 && !(commas === 1 && text.includes("."))) {
    return { text, caret };
  }
  let out = "";
  let shifted = caret;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === ",") {
      if (i < caret) shifted--;
      continue;
    }
    out += text[i];
  }
  return { text: out, caret: shifted };
}

/**
 * Regroup `text` by threes and map `caret` through the reflow.
 *
 * The caret is the whole difficulty: writing back a reformatted value parks it at
 * the end, so editing the middle of a number would jump on every keystroke. Count
 * the value-carrying characters left of the caret, then walk the formatted string
 * until that many are behind us — group separators coming and going stay
 * invisible.
 */
export function groupOnInput(
  rawText: string,
  rawCaret: number,
): { value: string; caret: number } {
  const { text, caret } = dropGroupingCommas(rawText, rawCaret);
  let digits = "";
  let decimals: string | null = null; // non-null once a decimal mark is seen
  let kept = 0;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i] ?? "";
    const isDigit = ch >= "0" && ch <= "9";
    const isMark = ch === "," || ch === ".";
    if (!isDigit && !isMark) continue;
    if (isMark) {
      if (decimals !== null) continue; // only the first mark counts
      decimals = "";
    } else if (decimals !== null) {
      if (decimals.length >= 2) continue; // cents, no further precision
      decimals += ch;
    } else {
      digits += ch;
    }
    if (i < caret) kept++;
  }

  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);
  const value = decimals === null ? grouped : grouped + DECIMAL_MARK + decimals;

  let position = 0;
  let seen = 0;
  while (position < value.length && seen < kept) {
    if (value[position] !== GROUP_SEPARATOR) seen++;
    position++;
  }
  // Never let the caret park just before a separator.
  while (position < value.length && value[position] === GROUP_SEPARATOR) position++;

  return { value, caret: position };
}

/**
 * Turn an API decimal string into what the input shows: `"10000.00"` →
 * `"10 000"`, `"10000.50"` → `"10 000,50"`. Cents are kept only when non-zero.
 */
export function formatAmountForInput(value: string | null | undefined): string {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  const [whole = "0", cents = ""] = Math.abs(n).toFixed(2).split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);
  const sign = n < 0 ? "-" : "";
  const fraction = cents === "00" ? "" : DECIMAL_MARK + cents;
  return sign + grouped + fraction;
}

/**
 * Turn what the input shows into what the API accepts: `"10 000,50"` →
 * `"10000.5"`. Returns null when there is no number left, so an emptied field
 * clears the value instead of failing DRF's DecimalField.
 */
export function amountToApi(display: string): string | null {
  const bare = display
    .replace(SPACES, "")
    .replace("$", "")
    .replace(DECIMAL_MARK, ".")
    .replace(/\.$/, "");
  if (bare === "" || bare === "-") return null;
  const n = Number(bare);
  return Number.isNaN(n) ? null : bare;
}
